import { createBrowserRouter, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AppShell } from './components/layout/AppShell';
import { RouteError } from './components/ErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import { useAuth } from './stores/authStore';
import { isManagerUser, isWorkspaceAdminUser } from './lib/roles';

const Dashboard      = lazy(() => import('./pages/Dashboard'));
const Logbook        = lazy(() => import('./pages/Logbook'));
const CreatorStudio  = lazy(() => import('./pages/CreatorStudio'));
// BP 16/03 — per-file People & access page.
const GuideAccessPage = lazy(() => import('./pages/GuideAccessPage'));
// BP 26-03 P1 — per-diagram People & access page, the FlowSnip twin of GuideAccessPage.
const FlowSnipAccessPage = lazy(() => import('./pages/FlowSnipAccessPage'));
const ChatPage       = lazy(() => import('./pages/ChatPage'));
const ProjectsPage      = lazy(() => import('./pages/ProjectsPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const WorksnipsExplorer = lazy(() => import('./pages/WorksnipsExplorer'));
// BP 26-03 — standalone subscription-gated process diagram builder.
const ProcessStudioPage = lazy(() => import('./pages/ProcessStudioPage'));
const Inbox          = lazy(() => import('./pages/Inbox'));
const Saved          = lazy(() => import('./pages/Saved'));
const SnipBoard      = lazy(() => import('./pages/SnipBoard'));
// BP 37 — Kanban view of typed snips (drag between work_status columns).
const KanbanBoard    = lazy(() => import('./components/snipboard/KanbanBoard').then(m => ({ default: m.KanbanBoard })));
const SharedWithMe   = lazy(() => import('./pages/SharedWithMe'));
const SettingsPage   = lazy(() => import('./pages/Settings'));
const FlickMode      = lazy(() => import('./pages/GuideViewer/FlickMode'));
const BriefMode      = lazy(() => import('./pages/GuideViewer/BriefMode'));
const ReelMode       = lazy(() => import('./pages/GuideViewer/ReelMode'));
const BeatMode       = lazy(() => import('./pages/GuideViewer/BeatMode'));
const PublicViewer   = lazy(() => import('./pages/PublicViewer'));
// BP 26-03 P2 — public (no-auth) password-protected FlowSnip/PageSnip share
// link viewer, the /fs/:token sibling of the guide's /g/:token PublicViewer.
const PublicFlowSnipViewer = lazy(() => import('./pages/PublicFlowSnipViewer'));
const AuthGoogleCallback = lazy(() => import('./pages/AuthGoogleCallback'));
const AuthMicrosoftCallback = lazy(() => import('./pages/AuthMicrosoftCallback'));
// Optional post-OAuth "name your workspace" step. OAuth callbacks create the
// org server-side (default "{name}'s workspace") before any form could ask, so
// brand-new federated signups (`?new=1` on the done URL) get ONE skippable
// screen to set a company name before the normal /choose-plan landing.
const AuthWorkspaceName = lazy(() => import('./pages/AuthWorkspaceName'));
// Email verification gate (password signups). Two pages:
//   /auth/verify        — landing page for the email link. Bounces the user
//                          into the backend's /api/v1/auth/verify endpoint.
//   /auth/verify-done   — terminal page; receives the JWT from the backend
//                          302, logs the user in, navigates to "/". Mirrors
//                          the existing /auth/google/done page.
const AuthVerify         = lazy(() => import('./pages/AuthVerify'));
const AuthVerifyDone     = lazy(() => import('./pages/AuthVerifyDone'));
// PR1 — /invites/:token accept page. No AppShell, no auth guard — handles
// three states: signed-in matching email → redirect to guide; signed-in
// mismatched email → "sign out and try again"; not signed in → prefilled
// signup / sign-in flow that lands on the guide after auth.
const InviteAccept   = lazy(() => import('./pages/InviteAccept'));
// Wave-9 / Change 3 — slim grid for /captures + /folders/:folderId. Replaces
// the placeholder that mounted Dashboard (full KPI hero) on those routes.
const CapturesPage   = lazy(() => import('./pages/CapturesPage'));
// Help & onboarding — role-scoped guides + the lifecycle swimlane.
const HelpCenter     = lazy(() => import('./pages/HelpCenter'));

// W4 — /admin/* is now ORG-admin scope; cross-tenant moved to /platform-admin/*.
const AdminLayout    = lazy(() => import('./pages/admin/AdminLayout'));
const AdminUsers     = lazy(() => import('./pages/admin/AdminUsers'));
const AdminGroups    = lazy(() => import('./pages/admin/AdminGroups'));
const AdminSettings  = lazy(() => import('./pages/admin/AdminSettings'));
// BP 43-02 — org-admin capture redaction (platform defaults + workspace patterns).
const AdminRedaction = lazy(() => import('./pages/admin/AdminRedaction'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const AdminBilling   = lazy(() => import('./pages/admin/AdminBilling'));
const AdminAudit     = lazy(() => import('./pages/admin/AdminAudit'));
// AI usage is a platform-admin surface now (cross-tenant). The org-scoped
// AdminAIUsage page was removed 2026-07-10 — its data still ships in the org
// Billing Statement export, which calls /org-admin/ai-usage directly.
// Approval history (migration 134) — BC-style immutable approval-entry log.
const AdminApprovalHistory = lazy(() => import('./pages/admin/AdminApprovalHistory'));
// BP 37 — work-tracking registries + analytics.
const AdminWorkTracking = lazy(() => import('./pages/admin/AdminWorkTracking'));
const AdminWorkAnalytics = lazy(() => import('./pages/admin/AdminWorkAnalytics'));
// BP 39 — learning analytics + cohorts/clients + My learning.
const AdminLearningAnalytics = lazy(() => import('./pages/admin/AdminLearningAnalytics'));
const AdminCohorts = lazy(() => import('./pages/admin/AdminCohorts'));
// BP 48 — management & allocation (adopted from TestoLogics): swimlane map,
// 3-pane allocation board, manager overview rollup. All manager-gated, additive.
const AdminAllocationMap      = lazy(() => import('./pages/admin/AdminAllocationMap'));
const AdminAllocationBoard    = lazy(() => import('./pages/admin/AdminAllocationBoard'));
const AdminAllocationOverview = lazy(() => import('./pages/admin/AdminAllocationOverview'));
// BP 48/54 — standalone Allocation surface layout (Overview/Board/Map sub-nav).
const AllocationLayout        = lazy(() => import('./pages/allocation/AllocationLayout'));
// BP 11-01 §7 — Embeddable Help Widget admin.
const AdminWidget  = lazy(() => import('./pages/admin/AdminWidget'));
// Worksnip Assistant — org-admin settings for the in-BC help assistant.
const AdminBcHelpAgent = lazy(() => import('./pages/admin/AdminBcHelpAgent'));
const AdminImportExport = lazy(() => import('./pages/admin/AdminImportExport')); // BP-55 — .wsp import/export
// Migration 103 — content approval review queue.
const AdminContentReview = lazy(() => import('./pages/admin/AdminContentReview'));
// Migration 104 — SAML SSO done page + SSO/SCIM admin.
const AuthSamlDone = lazy(() => import('./pages/AuthSamlDone'));
const AdminSSO     = lazy(() => import('./pages/admin/AdminSSO'));
// Migration 105 — organization white-label branding.
const AdminBranding = lazy(() => import('./pages/admin/AdminBranding'));
// Learning Center — consolidated tabbed surface. "My Learning" (trainee view,
// everyone) + manager tabs (Overview / People / Worksnips) gated on role. The
// former separate MyLearning + ManagerTraining pages are now its tab panels.
const LearningCenter = lazy(() => import('./pages/LearningCenter'));

const PlatformLayout            = lazy(() => import('./pages/platform-admin/PlatformLayout'));
const PlatformDashboard         = lazy(() => import('./pages/platform-admin/PlatformDashboard'));
const PlatformOrganizations     = lazy(() => import('./pages/platform-admin/PlatformOrganizations'));
const PlatformUsers             = lazy(() => import('./pages/platform-admin/PlatformUsers'));
const PlatformCaptures          = lazy(() => import('./pages/platform-admin/PlatformCaptures'));
const PlatformComments          = lazy(() => import('./pages/platform-admin/PlatformComments'));
const PlatformShareTraffic      = lazy(() => import('./pages/platform-admin/PlatformShareTraffic'));
const PlatformBilling           = lazy(() => import('./pages/platform-admin/PlatformBilling'));
const PlatformAIModels          = lazy(() => import('./pages/platform-admin/PlatformAIModels'));
const PlatformSubscriptionPlans = lazy(() => import('./pages/platform-admin/PlatformSubscriptionPlans'));
const PlatformAIUsage           = lazy(() => import('./pages/platform-admin/PlatformAIUsage'));
const PlatformAuditTrail        = lazy(() => import('./pages/platform-admin/PlatformAuditTrail'));
const PlatformSystemSettings    = lazy(() => import('./pages/platform-admin/PlatformSystemSettings'));
const PlatformSystemHealth      = lazy(() => import('./pages/platform-admin/PlatformSystemHealth'));
// Plan B / Sprint-18 — Business Central Knowledge Base admin.
const PlatformKnowledgeBase     = lazy(() => import('./pages/platform-admin/PlatformKnowledgeBase'));
const PlatformEmbeddings        = lazy(() => import('./pages/platform-admin/PlatformEmbeddings'));
// KB Research Pipeline — Tier-2 best-practice article viewer + insights.
const PlatformKbResearch        = lazy(() => import('./pages/platform-admin/PlatformKbResearch'));
const PlatformRagEval           = lazy(() => import('./pages/platform-admin/PlatformRagEval'));
const PlatformJobs              = lazy(() => import('./pages/platform-admin/PlatformJobs')); // BP 47 — build-queue jobs
// BP 38 — phases 2–4 platform-admin pages.
const PlatformFeatureFlags      = lazy(() => import('./pages/platform-admin/PlatformFeatureFlags'));
const PlatformTtsApi            = lazy(() => import('./pages/platform-admin/PlatformTtsApi'));
const PlatformAutomation        = lazy(() => import('./pages/platform-admin/PlatformAutomation'));
const PlatformContentApi        = lazy(() => import('./pages/platform-admin/PlatformContentApi'));
const PlatformCredits           = lazy(() => import('./pages/platform-admin/PlatformCredits'));
const PlatformInvitations       = lazy(() => import('./pages/platform-admin/PlatformInvitations'));
const PlatformSystemLogs        = lazy(() => import('./pages/platform-admin/PlatformSystemLogs'));
const PlatformIPSecurity        = lazy(() => import('./pages/platform-admin/PlatformIPSecurity'));
const PlatformPIIFilters        = lazy(() => import('./pages/platform-admin/PlatformPIIFilters'));
const PlatformEmail             = lazy(() => import('./pages/platform-admin/PlatformEmail'));
const PlatformStorage           = lazy(() => import('./pages/platform-admin/PlatformStorage'));
const PlatformAIConfig          = lazy(() => import('./pages/platform-admin/PlatformAIConfig'));
const PlatformWorksnipAssistant = lazy(() => import('./pages/platform-admin/PlatformWorksnipAssistant'));
const PlatformDataConsole       = lazy(() => import('./pages/platform-admin/PlatformDataConsole'));
const PlatformBackup            = lazy(() => import('./pages/platform-admin/PlatformBackup'));
// BP 07-03 — the second copy (Azure Blob) DR console: replica health,
// backfill progress, per-object failures, and the replica→primary restore.
const PlatformSecondCopy        = lazy(() => import('./pages/platform-admin/PlatformSecondCopy'));
const PlatformSOC2              = lazy(() => import('./pages/platform-admin/PlatformSOC2')); // BP 43-04 — SOC 2 readiness tracker
const ChoosePlan                = lazy(() => import('./pages/ChoosePlan'));
const WorkspaceInviteAccept     = lazy(() => import('./pages/WorkspaceInviteAccept'));

// Hard navigation (full reload) — used to cross workspace/basename boundaries
// (app ↔ public, or flat → workspace-prefixed) since an SPA <Navigate> would be
// rewritten by the active basename and miss the target.
function hardNav(to: string) {
  const here = window.location.pathname + window.location.search;
  if (here !== to) window.location.assign(to);
}

// PageSnip rename — `/flowsnip*` deep links (pre-rename bookmarks, emailed
// links, notification payloads) must keep resolving after the route moved to
// `/pagesnip*`. A plain <Navigate to="..."/> can't see the `:id` param, so
// this reads it via useParams and rebuilds the target path (+ optional
// suffix, e.g. "/access") under the new prefix.
function RedirectFlowsnipId({ suffix = '' }: { suffix?: string }) {
  const { id } = useParams();
  return <Navigate to={`/pagesnip/${id}${suffix}`} replace />;
}

function Guard({ children }: { children: React.ReactNode }) {
  const token = useAuth(s => s.token);
  const user = useAuth(s => s.user);
  const org = useAuth(s => s.organization);
  if (!token) { hardNav('/login'); return null; }
  // Always carry the workspace prefix. If the app rendered WITHOUT a basename
  // (e.g. a SPA login landed on flat '/'), hard-redirect into the home workspace.
  if (!BASENAME) {
    const homeId = useAuth.getState().activeOrgId || user?.organization_id;
    if (homeId) { hardNav(`/w/${homeId}${window.location.pathname === '/' ? '' : window.location.pathname}${window.location.search}`); return null; }
  }
  // Paywall onboarding gate — a brand-new workspace must complete a paid plan
  // before entering the app. Existing workspaces are grandfathered
  // (requires_plan_selection=false) and platform/super-admins always bypass.
  const isSuper = !!user?.is_super_admin;
  const needsPlan = !isSuper && !!org?.requires_plan_selection && org?.subscription_status !== 'active';
  if (needsPlan) { hardNav('/choose-plan'); return null; }
  return <>{children}</>;
}

// Token-only guard for the onboarding paywall page itself — must NOT apply the
// plan gate or it would redirect-loop.
function TokenGuard({ children }: { children: React.ReactNode }) {
  const token = useAuth(s => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// W4 — role gate. Mirrors the backend's requireRole(...allowedRoles) but in
// React. The backend is the authority; this is just a soft frontend redirect
// to keep the URL bar honest and avoid showing scaffolding to the wrong role.
type Role = 'platform_admin' | 'org_admin' | 'manager' | 'user';
function RequireRole({ allowed, children }: { allowed: Role[]; children: ReactNode }) {
  const user = useAuth(s => s.user);
  // Most-specific authority first. A pure Work Manager resolves to 'manager';
  // AdminLayout then shows them only the manager slice (cohorts/teams +
  // analytics) and bounces admin-only sub-pages. Mirrors backend requireManager.
  const role: Role = user?.is_super_admin ? 'platform_admin'
    : isWorkspaceAdminUser(user) ? 'org_admin'
    : isManagerUser(user) ? 'manager'
    : 'user';
  if (!allowed.includes(role)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// BP 56 — a tiny component (not a bare `t()` call) so it re-renders on a
// language change like any other `useTranslation` consumer, even though it
// typically shows for only a frame or two while a lazy chunk loads.
function SuspenseFallback() {
  const { t } = useTranslation('common');
  return <div className="p-8 text-ws-ink-3">{t('common:status.loading')}</div>;
}
const wrap = (el: React.ReactNode) => <Suspense fallback={<SuspenseFallback />}>{el}</Suspense>;

// ── Workspace-scoped URLs via a dynamic basename (`/w/<orgId>`) ────────────
// The active workspace lives in the URL as `/w/<orgId>/...`. We use React
// Router's BASENAME (derived from the URL at load) rather than a path param, so
// every existing `to="/x"` resolves to `/w/<id>/x` AND `useLocation().pathname`
// stays `/x` — zero link / pathname-comparison churn across the app. Entering /
// switching / leaving a workspace is a HARD navigation (already how switch
// worked), which reloads and recomputes the basename. The backend still enforces
// tenancy from the X-Workspace-Id header — the URL is UX, not a security boundary.
function workspaceBasename(): string {
  const m = /^\/w\/([^/]+)/.exec(window.location.pathname);
  return m ? `/w/${m[1]}` : '';
}
export const BASENAME = workspaceBasename();

// Module-load bootstrap (runs before first render):
//  • Inside a workspace URL → sync its id into the store (apiClient header).
//  • Authed user on a bare app path (no prefix, not public) → hard-redirect into
//    their home workspace so the URL always carries the org id.
(() => {
  const st = useAuth.getState();
  if (BASENAME) {
    const id = BASENAME.slice(3); // strip "/w/"
    const home = st.user?.organization_id;
    const desired = id === home ? null : id;
    if (st.activeOrgId !== desired) st.setActiveOrg(desired);
    return;
  }
  const path = window.location.pathname;
  // BP 26-03 P2 — 'fs/' (the FlowSnip public share viewer) added alongside
  // 'g/' (its guide-viewer sibling) so a signed-in user opening a forwarded
  // /fs/:token link isn't hard-redirected into /w/<homeId>/fs/:token first.
  const isPublic = /^\/(login|signup|g\/|fs\/|invites\/|auth\/|choose-plan|workspace-invite)(\/|$)/.test(path);
  // Default workspace — prefer activeOrgId (seeded by setAuth() from
  // user.default_workspace_id at login, see authStore.ts) over the immutable
  // home org, so a user who pinned a non-home default actually lands there on
  // the very first redirect instead of the URL bouncing home → default a beat
  // later. Mirrors the same activeOrgId-first fallback the Guard component
  // uses below for the BASENAME-less case.
  const homeId = st.activeOrgId || st.user?.organization_id;
  if (st.token && homeId && !isPublic) {
    const rel = path === '/' ? '' : path;
    window.location.replace(`/w/${homeId}${rel}${window.location.search}${window.location.hash}`);
  }
})();

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // /signup renders the same combined auth page in register mode (the marketing
  // site's "Sign up" CTA links here). Avoids a 404 for app.worksnip.com/signup.
  { path: '/signup', element: <LoginPage /> },
  { path: '/auth/google/done', element: wrap(<AuthGoogleCallback />) },
  { path: '/auth/microsoft/done', element: wrap(<AuthMicrosoftCallback />) },
  // Post-OAuth workspace naming — token-gated but OUTSIDE the plan gate (it
  // runs BEFORE /choose-plan for new signups; Guard would bounce it). The
  // /auth/ prefix also keeps it out of the module-load workspace-prefix
  // redirect (see the isPublic regex above).
  { path: '/auth/workspace-name', element: wrap(<TokenGuard><AuthWorkspaceName /></TokenGuard>) },
  // Paywall onboarding — token-gated but OUTSIDE the AppShell plan gate.
  { path: '/choose-plan', element: wrap(<TokenGuard><ChoosePlan /></TokenGuard>) },
  { path: '/workspace-invite', element: wrap(<WorkspaceInviteAccept />) },
  { path: '/auth/verify',      element: wrap(<AuthVerify />) },
  { path: '/auth/verify-done', element: wrap(<AuthVerifyDone />) },
  { path: '/auth/saml/done',   element: wrap(<AuthSamlDone />) },
  { path: '/g/:token', element: wrap(<PublicViewer />) },
  // BP 26-03 P2 — password-protected FlowSnip/PageSnip public share link.
  { path: '/fs/:token', element: wrap(<PublicFlowSnipViewer />) },
  { path: '/invites/:token', element: wrap(<InviteAccept />) },
  {
    path: '/',
    element: <Guard><AppShell /></Guard>,
    // Backstop for errors thrown above the in-shell <Outlet/> boundary (AppShell
    // itself, guards, loaders). Per-page render errors are caught in-place by the
    // ErrorBoundary around the Outlet (keeps the nav), so this rarely fires.
    errorElement: <RouteError />,
    children: [
      { index: true,             element: wrap(<Dashboard />) },
      { path: 'dashboard',       element: wrap(<Dashboard />) },
      // Wave-9 — slim CapturesPage replaces the Dashboard placeholder. Title
      // resolves from the route param via useProjectTree; renders just a
      // search + lens chips + grid (NO KPI hero, NO Resume tile, NO Modes).
      { path: 'captures',                element: wrap(<CapturesPage />) },
      { path: 'folders/:folderId',       element: wrap(<CapturesPage />) },
      { path: 'logbook',         element: wrap(<Logbook />) },
      { path: 'board',           element: wrap(<SnipBoard />) },
      { path: 'board/kanban',    element: wrap(<KanbanBoard />) },
      { path: 'shared',          element: wrap(<SharedWithMe />) },
      { path: 'watchlist',       element: wrap(<Saved />) },
      // Legacy alias — the feature was renamed Saved → Watchlist.
      { path: 'saved',           element: <Navigate to="/watchlist" replace /> },
      { path: 'learning',        element: wrap(<LearningCenter />) },
      // Old "/training" manager cockpit folded into Learning Center's manager
      // tabs. Redirect any bookmarks to the Overview tab. The page itself
      // self-handles `manages:false` with an empty state.
      { path: 'training',        element: <Navigate to="/learning?tab=overview" replace /> },
      { path: 'projects',        element: wrap(<ProjectsPage />) },
      { path: 'projects/:id',    element: wrap(<ProjectDetailPage />) },
      { path: 'worksnips',       element: wrap(<WorksnipsExplorer />) },
      // PageSnip (formerly FlowSnip) — same components, new user-visible path.
      { path: 'pagesnip',        element: wrap(<ProcessStudioPage />) },
      { path: 'pagesnip/:id',    element: wrap(<ProcessStudioPage />) },   // deep-link open in the viewer
      { path: 'pagesnip/:id/access', element: wrap(<FlowSnipAccessPage />) },
      // Legacy `/flowsnip*` — kept as id-preserving redirects so pre-rename
      // bookmarks/notifications/deep-links still resolve (never 404, never
      // dead-end on the list page).
      { path: 'flowsnip',        element: <Navigate to="/pagesnip" replace /> },
      { path: 'flowsnip/:id',    element: <RedirectFlowsnipId /> },
      { path: 'flowsnip/:id/access', element: <RedirectFlowsnipId suffix="/access" /> },
      { path: 'process-studio',  element: <Navigate to="/pagesnip" replace /> },   // renamed → PageSnip
      { path: 'guides/:id',      element: wrap(<CreatorStudio />) },
      { path: 'guides/:id/access', element: wrap(<GuideAccessPage />) },
      { path: 'guides/:id/flick',  element: wrap(<FlickMode />) },
      { path: 'guides/:id/brief',  element: wrap(<BriefMode />) },
      { path: 'guides/:id/reel',   element: wrap(<ReelMode />) },
      { path: 'guides/:id/beat',   element: wrap(<BeatMode />) },
      { path: 'chat',            element: wrap(<ChatPage />) },
      { path: 'inbox',           element: wrap(<Inbox />) },
      { path: 'settings',        element: wrap(<SettingsPage />) },
      { path: 'help',            element: wrap(<HelpCenter />) },

      // W4 — /admin/* is the ORG-admin surface. Work Managers are admitted too
      // (AdminLayout shows them only the cohorts/teams + analytics slice and
      // redirects admin-only sub-pages; the backend enforces the real gates).
      {
        path: 'admin',
        element: <RequireRole allowed={['org_admin', 'platform_admin', 'manager']}>{wrap(<AdminLayout />)}</RequireRole>,
        children: [
          { index: true,             element: wrap(<AdminUsers />) },
          { path: 'users',           element: wrap(<AdminUsers />) },
          { path: 'groups',          element: wrap(<AdminGroups />) },
          { path: 'settings',        element: wrap(<AdminSettings />) },
          // BP 43-02 — capture redaction (platform defaults + org patterns).
          { path: 'redaction',       element: wrap(<AdminRedaction />) },
          { path: 'notifications',   element: wrap(<AdminNotifications />) },
          { path: 'billing',         element: wrap(<AdminBilling />) },
          { path: 'audit',           element: wrap(<AdminAudit />) },
          // Migration 134 — approval-entry history (BC-style approval log).
          { path: 'approval-history', element: wrap(<AdminApprovalHistory />) },
          // BP 37 — work-tracking registries + analytics dashboard.
          { path: 'work-tracking',   element: wrap(<AdminWorkTracking />) },
          { path: 'work-analytics',  element: wrap(<AdminWorkAnalytics />) },
          // BP 39 — learning analytics + cohorts/clients.
          { path: 'learning-analytics', element: wrap(<AdminLearningAnalytics />) },
          { path: 'cohorts',            element: wrap(<AdminCohorts />) },
          // BP 48/54 — Allocation moved to the standalone /allocation surface;
          // keep these as redirects so old bookmarks + links still resolve.
          { path: 'allocation-overview', element: <Navigate to="/allocation" replace /> },
          { path: 'allocation-board',    element: <Navigate to="/allocation/board" replace /> },
          { path: 'allocation-map',      element: <Navigate to="/allocation/map" replace /> },
          // BP 11-01 §7 — Embeddable Help Widget.
          { path: 'widget',             element: wrap(<AdminWidget />) },
          // Worksnip Assistant — in-BC help assistant settings.
          { path: 'worksnip-assistant', element: wrap(<AdminBcHelpAgent />) },
          // BP-55 — .wsp portable bundle import/export (moved off Creator Studio).
          { path: 'import-export',      element: wrap(<AdminImportExport />) },
          // Migration 103 — content approval review queue.
          { path: 'content-review',     element: wrap(<AdminContentReview />) },
          // Migration 104 — SSO & SCIM configuration.
          { path: 'sso',                element: wrap(<AdminSSO />) },
          // Migration 105 — organization white-label branding.
          { path: 'branding',           element: wrap(<AdminBranding />) },
        ],
      },

      // BP 48/54 — Allocation as a STANDALONE surface (was /admin/allocation-*).
      // Same access predicate as the old /admin allocation routes (RequireRole +
      // AllocationLayout's isManagerUser gate); backend requireManager on
      // /org-admin/assignments* is the authority. Managers + Workspace Admins.
      {
        path: 'allocation',
        element: <RequireRole allowed={['org_admin', 'platform_admin', 'manager']}>{wrap(<AllocationLayout />)}</RequireRole>,
        children: [
          { index: true,   element: wrap(<AdminAllocationOverview />) },
          { path: 'board', element: wrap(<AdminAllocationBoard />) },
          { path: 'map',   element: wrap(<AdminAllocationMap />) },
        ],
      },

      // W4 — /platform-admin/* is the cross-tenant surface. Platform admin only.
      {
        path: 'platform-admin',
        element: <RequireRole allowed={['platform_admin']}>{wrap(<PlatformLayout />)}</RequireRole>,
        children: [
          { index: true,                     element: wrap(<PlatformDashboard />) },
          { path: 'organizations',           element: wrap(<PlatformOrganizations />) },
          { path: 'users',                   element: wrap(<PlatformUsers />) },
          { path: 'captures',                element: wrap(<PlatformCaptures />) },
          { path: 'comments',                element: wrap(<PlatformComments />) },
          { path: 'public-analytics',        element: wrap(<PlatformShareTraffic />) },
          // Kept so the older /share-traffic link does not 404 for anyone
          // who bookmarked it before the page grew past traffic-only.
          { path: 'share-traffic',           element: wrap(<PlatformShareTraffic />) },
          { path: 'billing',                 element: wrap(<PlatformBilling />) },
          { path: 'ai-models',               element: wrap(<PlatformAIModels />) },
          { path: 'subscription-plans',      element: wrap(<PlatformSubscriptionPlans />) },
          { path: 'ai-usage',                element: wrap(<PlatformAIUsage />) },
          // Legacy path kept as a redirect — bookmarks predate the 2026-07-10 rename.
          { path: 'token-usage',             element: <Navigate to="/platform-admin/ai-usage" replace /> },
          { path: 'audit',                   element: wrap(<PlatformAuditTrail />) },
          { path: 'system-settings',         element: wrap(<PlatformSystemSettings />) },
          { path: 'system-health',           element: wrap(<PlatformSystemHealth />) },
          // Plan B / Sprint-18 — BC Knowledge Base management.
          { path: 'knowledge-base',          element: wrap(<PlatformKnowledgeBase />) },
          // KB Research Pipeline — Tier-2 best-practice article reviews + insights.
          { path: 'kb-research',             element: wrap(<PlatformKbResearch />) },
          // RAG evaluation — retrieval + answer-quality scorecard + pipeline inspector.
          { path: 'rag-eval',                element: wrap(<PlatformRagEval />) },
          // BP 47 — background-job ledger (build-queue observability).
          { path: 'jobs',                    element: wrap(<PlatformJobs />) },
          // Embeddings & RAG — provider config + batch/reindex over KB chunks.
          { path: 'embeddings',              element: wrap(<PlatformEmbeddings />) },
          // BP 38 — phases 2–4.
          { path: 'feature-flags',           element: wrap(<PlatformFeatureFlags />) },
          { path: 'automation',              element: wrap(<PlatformAutomation />) },
          { path: 'tts-api',                 element: wrap(<PlatformTtsApi />) },
          { path: 'content-api',             element: wrap(<PlatformContentApi />) },
          { path: 'credits',                 element: wrap(<PlatformCredits />) },
          { path: 'invitations',             element: wrap(<PlatformInvitations />) },
          { path: 'system-logs',             element: wrap(<PlatformSystemLogs />) },
          { path: 'ip-security',             element: wrap(<PlatformIPSecurity />) },
          { path: 'pii-filters',             element: wrap(<PlatformPIIFilters />) },
          { path: 'email',                   element: wrap(<PlatformEmail />) },
          { path: 'storage',                 element: wrap(<PlatformStorage />) },
          { path: 'ai-config',               element: wrap(<PlatformAIConfig />) },
          // Worksnip Assistant — platform-wide AI model for the in-BC help assistant.
          { path: 'worksnip-assistant',      element: wrap(<PlatformWorksnipAssistant />) },
          { path: 'data-console',            element: wrap(<PlatformDataConsole />) },
          { path: 'backups',                 element: wrap(<PlatformBackup />) },
          { path: 'second-copy',             element: wrap(<PlatformSecondCopy />) },
          { path: 'soc2',                    element: wrap(<PlatformSOC2 />) },
        ],
      },
    ],
  },
], { basename: BASENAME });
